import { useParams, useNavigate, Link } from "react-router-dom";
import { Phone, MapPin, Video, Globe2, Sparkles, CalendarClock } from "lucide-react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEO, buildLegalServiceSchema, buildBreadcrumbSchema } from "@/components/seo/SEO";
import { Watermark } from "@/components/shared/Watermark";
import { CTAButton } from "@/components/shared/CTAButton";
import { Breadcrumbs } from "@/components/shared/Breadcrumbs";
import { MotionReveal, MotionStagger, MotionStaggerItem } from "@/components/shared/MotionReveal";
import { DynamicIcon } from "@/components/shared/IconMap";
import { CaseEvaluationDialog } from "@/components/home/CaseEvaluationDialog";
import { TestimonialsCarousel } from "@/components/home/TestimonialsCarousel";
import { FIRM, PRACTICE_AREAS_NAV, CITIES, PAGE_HERO_IMAGES } from "@/constants/firmInfo";
import { useLanguage, fillTemplate } from "@/i18n/LanguageContext";
import { PRACTICE_AREAS_ES } from "@/i18n/translations";

const WHY_REASON_ICONS = [Video, Globe2, Sparkles, CalendarClock];

export default function LocationDetail() {
  const { city: citySlug } = useParams();
  const navigate = useNavigate();
  const { t, isSpanish } = useLanguage();

  const city = CITIES.find((c) => c.slug === citySlug);

  if (!city) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="location-not-found">
        <h1 className="font-heading text-2xl font-semibold text-[color:var(--navy-950)]">{t("locations.notFoundHeading")}</h1>
        <div className="mt-6 flex justify-center">
          <CTAButton variant="primary" onClick={() => navigate("/")}>{t("locations.backToHome")}</CTAButton>
        </div>
      </div>
    );
  }

  const vars = { city: city.name, distance: isSpanish ? city.distanceEs : city.distanceEn };
  const whyReasons = t("locations.whyReasons");
  const faqs = t("locations.faq");
  const practiceAreaTitle = (area) => (isSpanish ? PRACTICE_AREAS_ES[area.slug]?.title || area.title : area.title);

  return (
    <article data-testid="location-detail-page">
      <SEO
        title={fillTemplate(t("seo.location.title"), vars)}
        description={fillTemplate(t("seo.location.description"), vars)}
        path={`/locations/${city.slug}`}
        jsonLd={[
          buildLegalServiceSchema(),
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Locations", path: "/" },
            { name: city.name, path: `/locations/${city.slug}` },
          ]),
        ]}
      />

      <header className="relative bg-[color:var(--navy-950)] hero-noise overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(11,18,32,0.92) 0%, rgba(11,18,32,0.85) 55%, rgba(11,18,32,0.96) 100%), url(${PAGE_HERO_IMAGES.location})`,
            backgroundSize: "cover",
            backgroundPosition: "center 45%",
          }}
        />
        <Watermark word={city.name} position="top-right" className="text-white top-2" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(900px 500px at 85% 0%, rgba(184,154,61,0.14), transparent 55%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <Breadcrumbs
            items={[
              { label: t("locations.breadcrumbLocations"), to: "/" },
              { label: city.name, to: `/locations/${city.slug}` },
            ]}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20 pt-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-400)]">
              <MapPin className="h-3.5 w-3.5" /> {t("locations.eyebrow")}
            </span>
            <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-white text-balance">
              {fillTemplate(t("locations.heroHeading"), vars)}
            </h1>
            <p className="mt-4 text-slate-300 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
              {fillTemplate(t("locations.heroSubtitle"), vars)}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <CaseEvaluationDialog
                trigger={<CTAButton variant="brass" data-testid="location-hero-cta-button">{t("practiceAreaDetail.heroCta")}</CTAButton>}
              />
              <a href={`tel:${FIRM.phoneTel}`} data-testid="location-hero-phone-link">
                <CTAButton variant="outline" icon={Phone}>{FIRM.phoneDisplay}</CTAButton>
              </a>
            </div>
          </motion.div>
        </div>
      </header>

      <section className="py-16 sm:py-20 bg-[color:var(--paper)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <MotionReveal className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl text-[color:var(--navy-950)]">{fillTemplate(t("locations.whyHeading"), vars)}</h2>
          </MotionReveal>
          <MotionStagger className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {whyReasons.map((reason, idx) => {
              const ReasonIcon = WHY_REASON_ICONS[idx] || Sparkles;
              return (
                <MotionStaggerItem key={reason}>
                  <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-[color:var(--sand)] p-4 h-full">
                    <div className="h-9 w-9 rounded-full bg-[color:var(--navy-950)] grid place-items-center shrink-0">
                      <ReasonIcon className="h-4 w-4 text-[color:var(--gold-400)]" />
                    </div>
                    <span className="text-sm text-slate-700 font-medium">{fillTemplate(reason, vars)}</span>
                  </div>
                </MotionStaggerItem>
              );
            })}
          </MotionStagger>
        </div>
      </section>

      <section className="relative py-16 sm:py-20 bg-[color:var(--sand)] overflow-hidden">
        <Watermark word="Counsel" position="bottom-left" className="text-[color:var(--navy-950)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MotionReveal className="max-w-2xl mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl text-[color:var(--navy-950)]">{fillTemplate(t("locations.practiceHeading"), vars)}</h2>
          </MotionReveal>
          <MotionStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRACTICE_AREAS_NAV.map((area) => (
              <MotionStaggerItem key={area.slug}>
                <Link
                  to={`/practice-areas/${area.slug}`}
                  className="group flex items-center gap-4 rounded-2xl border border-slate-200/70 bg-white p-5 h-full shadow-[0_10px_30px_rgba(11,18,32,0.05)] hover:shadow-[0_18px_50px_rgba(11,18,32,0.12)] hover:-translate-y-0.5 transition-all duration-300"
                  data-testid={`location-practice-area-${area.slug}`}
                >
                  <div className="h-11 w-11 shrink-0 rounded-2xl bg-[color:var(--navy-950)] grid place-items-center group-hover:bg-[color:var(--gold-500)] transition-colors duration-300">
                    <DynamicIcon name={area.icon} className="h-5 w-5 text-[color:var(--gold-400)] group-hover:text-[color:var(--navy-950)] transition-colors duration-300" />
                  </div>
                  <span className="font-heading text-base text-[color:var(--navy-950)] group-hover:text-[color:var(--gold-500)] transition-colors">{practiceAreaTitle(area)}</span>
                </Link>
              </MotionStaggerItem>
            ))}
          </MotionStagger>
        </div>
      </section>

      <TestimonialsCarousel />

      <section className="py-16 sm:py-20 bg-[color:var(--paper-2)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <MotionReveal>
            <h2 className="font-heading text-2xl sm:text-3xl text-[color:var(--navy-950)] mb-6">{t("locations.faqHeading")}</h2>
          </MotionReveal>
          <Accordion type="single" collapsible className="bg-white rounded-3xl border border-slate-200 px-6 shadow-[0_10px_30px_rgba(11,18,32,0.05)]" data-testid="location-faq-accordion">
            {faqs.map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`}>
                <AccordionTrigger className="text-left font-medium text-[color:var(--navy-950)]" data-testid={`location-faq-question-${idx}`}>
                  {fillTemplate(faq.question, vars)}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{fillTemplate(faq.answer, vars)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-[color:var(--navy-950)] hero-noise relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(900px 500px at 50% 0%, rgba(184,154,61,0.14), transparent 60%)" }}
        />
        <MotionReveal className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl text-white">{fillTemplate(t("locations.ctaHeading"), vars)}</h2>
          <p className="mt-3 text-slate-300">{t("locations.ctaSubtitle")}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <CaseEvaluationDialog
              trigger={<CTAButton variant="brass">{t("practiceAreaDetail.ctaPrimary")}</CTAButton>}
            />
            <Link to="/contact" data-testid="location-cta-contact-link">
              <CTAButton variant="outline">{t("practiceAreaDetail.ctaSecondary")}</CTAButton>
            </Link>
          </div>
        </MotionReveal>
      </section>
    </article>
  );
}
